import React, { useState, useEffect } from 'react';
import axios from 'axios';
import constant from '../constants/constant';
import { Gift, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';

const RewardsTracking = () => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [confirmModal, setConfirmModal] = useState({ visible: false, id: null, status: null });
  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3000);
  };

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/admin/rewards/pending`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) setRewards(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!confirmModal.id || !confirmModal.status) return;
    try {
      await axios.put(`${constant.API_BASE_URL}/admin/rewards/${confirmModal.id}/status`, { status: confirmModal.status }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      showToast(`Reward successfully marked as ${confirmModal.status}`);
      setConfirmModal({ visible: false, id: null, status: null });
      fetchRewards();
    } catch (err) {
      showToast("Failed to update status", 'error');
      setConfirmModal({ visible: false, id: null, status: null });
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Pending Rewards</h2>
          <p className="text-slate-500 mt-1">Track the 30% locked rewards. Manually hold or cancel them if fraud is detected.</p>
        </div>
        <button onClick={fetchRewards} className="flex items-center gap-2 bg-white text-slate-700 px-5 py-2.5 rounded-xl font-semibold shadow-sm border border-gray-200 hover:bg-gray-50 transition">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-100/30 border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-20 text-center text-slate-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">User</th>
                  <th className="p-6">Level / Total (💎)</th>
                  <th className="p-6">Locked Amount (💎)</th>
                  <th className="p-6">Release Date</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {rewards.map(reward => (
                  <tr key={reward.id} className="hover:bg-slate-50/50 transition">
                    <td className="p-6 font-bold text-slate-800">
                      {reward.first_name} {reward.last_name}
                      <p className="text-xs font-normal text-slate-400">{reward.email}</p>
                    </td>
                    <td className="p-6">
                      Level {reward.target_level}
                      <p className="text-xs text-slate-400">Total: {parseInt(reward.total_reward).toLocaleString()}</p>
                    </td>
                    <td className="p-6 font-extrabold text-pink-600">{parseInt(reward.locked_amount).toLocaleString()}</td>
                    <td className="p-6 text-slate-600">{new Date(reward.release_date).toLocaleString()}</td>
                    <td className="p-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        reward.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                        reward.status === 'CREDITED' ? 'bg-green-100 text-green-700' :
                        reward.status === 'HOLD' ? 'bg-orange-100 text-orange-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {reward.status}
                      </span>
                    </td>
                    <td className="p-6 text-right space-x-2">
                      {reward.status === 'PENDING' && (
                        <>
                          <button onClick={() => setConfirmModal({ visible: true, id: reward.id, status: 'HOLD' })} className="px-3 py-1.5 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 text-xs font-bold transition">Hold</button>
                          <button onClick={() => setConfirmModal({ visible: true, id: reward.id, status: 'CANCELLED' })} className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-xs font-bold transition">Cancel</button>
                        </>
                      )}
                      {reward.status === 'HOLD' && (
                        <button onClick={() => setConfirmModal({ visible: true, id: reward.id, status: 'PENDING' })} className="px-3 py-1.5 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 text-xs font-bold transition">Un-hold</button>
                      )}
                    </td>
                  </tr>
                ))}
                {rewards.length === 0 && (
                  <tr><td colSpan="6" className="p-10 text-center text-slate-500">No rewards pending.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Confirmation Modal */}
      {confirmModal.visible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-sm p-6 text-center animate-in zoom-in-95 duration-200">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={32} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Confirm Action</h3>
            <p className="text-slate-500 text-sm mb-6">Are you sure you want to mark this reward as <span className="font-bold text-slate-700">{confirmModal.status}</span>? This action can be modified later.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setConfirmModal({ visible: false, id: null, status: null })} className="px-6 py-2.5 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition">Cancel</button>
              <button onClick={handleUpdateStatus} className="px-6 py-2.5 bg-pink-600 text-white rounded-xl font-bold hover:bg-pink-700 transition">Confirm Update</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-6 py-3 rounded-xl shadow-lg border text-sm font-bold flex items-center gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300 ${toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-700 border-green-100'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default RewardsTracking;
